import { GenericAbortSignal } from "axios";
import api from "PFCore/api";
import { RequestConfig } from "PFCore/api/api";
import { decamelizeKeysExcept } from "PFCore/helpers/keys_camelization_utils";
import { ExportResponse } from "PFCore/services/common";
import {
  AuditRole,
  Id,
  ManageRolesViewRoleItem,
  PaginatedCollection,
  QueryParams,
  Role,
  WorkflowActivity
} from "PFTypes";
import { AvailabilityMode } from "PFTypes/booking";
import { DOC_FORMAT_MIME_TYPE, DocFormat } from "PFTypes/doc_format";

export type FetchRolesParams = QueryParams<{
  id?: number[];
  parentActivityId?: number;
  state?: string[];
  workflow_state?: string[];
}>;

export type RolesResponseItem = { activity: Role; id: number };

export type FetchRolesResponse = PaginatedCollection<RolesResponseItem[]>;

export const fetchRolesDocs = (docType: DocFormat, params: FetchRolesParams): Promise<ExportResponse> =>
  api({
    url: "roles",
    params,
    headers: {
      Accept: DOC_FORMAT_MIME_TYPE[docType]
    }
  });

export const fetchRoles = (
  params: FetchRolesParams,
  { signal }: { signal?: GenericAbortSignal }
): Promise<FetchRolesResponse> =>
  api({
    url: "roles",
    params,
    signal
  });

// the endpoint does not apply segment filters
export const fetchNestedActivities = (
  params: FetchRolesParams,
  { signal }: { signal?: GenericAbortSignal }
): Promise<FetchRolesResponse> =>
  api({
    url: "nested_activities",
    params,
    signal
  });

export type FetchAuditRolesParams = QueryParams & { searchId?: number; forceNewSearch?: boolean };
export type FetchAuditRolesResponse = PaginatedCollection<{ activity: AuditRole; id: number }[]>;

export const fetchAuditRoles = (
  params: FetchAuditRolesParams,
  config?: RequestConfig
): Promise<FetchAuditRolesResponse> =>
  api({
    url: "audit_roles",
    params,
    ...(config ?? {})
  });

export type FetchRolesIndexParams = QueryParams;
export type FetchRolesIndexResponse = PaginatedCollection<WorkflowActivity[]>;

export const fetchRolesIndex = (data: FetchRolesIndexParams) =>
  api<FetchRolesIndexResponse>({
    url: "roles/index",
    data,
    method: "POST"
  }).then((res) =>
    decamelizeKeysExcept(res, ["meta.filtersV2", "meta.searchId", "meta.perPage", "meta.totalPages"])
  );

export type FetchManageViewRolesParams = QueryParams<
  unknown,
  { targetAvailabilityMode: AvailabilityMode; activityIds?: Id[] }
>;

export type ManageViewRolesCounters = {
  fillAndBookableVacancies: number;
};

export type FetchManageViewRolesResponse = PaginatedCollection<
  ManageRolesViewRoleItem[],
  object,
  ManageViewRolesCounters
>;

export const fetchManageViewRoles = (
  params: FetchManageViewRolesParams
): Promise<FetchManageViewRolesResponse> =>
  api({
    url: "/roles/manage_view",
    data: params,
    method: "POST"
  });
